import { effectiveCpmRateCents } from './cpmOverrideOptions';
import type { SubmissionVerdictKey } from './getSubmissionVerdictKey';

/**
 * Per-clip estimate matching backend `calculateTargetPayoutCents` for a
 * single submission: threshold is an eligibility gate, then
 * floor(views * rate / 1000), then the per-clip cap. Clipper/budget caps
 * are campaign-level and already surfaced separately on this page.
 */
export function estimateSubmissionPayoutCents({
  views,
  cpmRateCents,
  minViewsThreshold,
  maxPayoutPerClipCents,
}: {
  views: number;
  cpmRateCents: number;
  minViewsThreshold?: number | null;
  maxPayoutPerClipCents?: number | null;
}): number {
  const safeViews = Number.isFinite(views) ? Math.max(0, views) : 0;
  const threshold = minViewsThreshold || 0;
  if (safeViews < threshold) return 0;

  const rate = Number.isFinite(cpmRateCents) ? Math.max(0, cpmRateCents) : 0;
  let target = Math.floor((safeViews * rate) / 1000);

  if (maxPayoutPerClipCents != null && maxPayoutPerClipCents > 0) {
    target = Math.min(target, maxPayoutPerClipCents);
  }

  return Math.max(0, target);
}

export type SubmissionEarningsKind = 'confirmed' | 'potential' | 'none';

export type SubmissionEarningsDisplay = {
  cents: number;
  kind: SubmissionEarningsKind;
  viewsRemainingToThreshold: number;
};

export function resolveSubmissionEarningsDisplay({
  verdict,
  views,
  accumulatedPayoutCents,
  campaignCpmRateCents,
  overrideCents,
  minViewsThreshold,
  maxPayoutPerClipCents,
}: {
  verdict: SubmissionVerdictKey;
  views: number;
  accumulatedPayoutCents?: number | null;
  campaignCpmRateCents: number;
  overrideCents?: number | null;
  minViewsThreshold?: number | null;
  maxPayoutPerClipCents?: number | null;
}): SubmissionEarningsDisplay {
  if (verdict === 'disapproved' || verdict === 'disqualified') {
    return { cents: 0, kind: 'none', viewsRemainingToThreshold: 0 };
  }

  const safeViews = Number.isFinite(views) ? Math.max(0, views) : 0;
  const threshold = minViewsThreshold || 0;
  const viewsRemainingToThreshold = Math.max(0, threshold - safeViews);

  if (verdict === 'approved') {
    return {
      cents: accumulatedPayoutCents || 0,
      kind: 'confirmed',
      viewsRemainingToThreshold,
    };
  }

  return {
    cents: estimateSubmissionPayoutCents({
      views: safeViews,
      cpmRateCents: effectiveCpmRateCents(
        { cpm_rate_cents: campaignCpmRateCents },
        overrideCents,
      ),
      minViewsThreshold: threshold,
      maxPayoutPerClipCents,
    }),
    kind: 'potential',
    viewsRemainingToThreshold,
  };
}
